import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../dashboard/components/AdminLayout';
import { useAdminETLTasks } from '../hooks/useAdminETLTasks';
import { StatCard } from '../../dashboard/components/StatCard';
import { adminETLService } from '../services/admin-etl.service';
import { COLORS } from '../../../../core/constants/colors';

export const AdminETLTasksPage: React.FC = () => {
  const { tasks, statistics, isLoading, refetch } = useAdminETLTasks();
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isRetrying, setIsRetrying] = useState<number | null>(null);
  const itemsPerPage = 10;

  const filteredTasks = tasks.filter((task) => {
    const matchesSearch = searchQuery === '' ||
      task.id.toString().includes(searchQuery) ||
      task.document_id.toString().includes(searchQuery);
    const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const paginatedTasks = filteredTasks.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredTasks.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return { bg: '#D1FAE5', text: '#059669' };
      case 'processing':
        return { bg: '#FEF3C7', text: '#D97706' };
      case 'failed':
        return { bg: '#FFEBEE', text: '#DC2626' };
      case 'pending':
        return { bg: '#DBEAFE', text: '#1E40AF' };
      default:
        return { bg: '#F3F4F6', text: '#6B7280' };
    }
  };

  const handleRetry = async (taskId: number) => {
    setIsRetrying(taskId);
    try {
      const success = await adminETLService.retryTask(taskId);
      if (success) {
        await refetch();
      }
    } catch (error) {
      console.error('Error retrying task:', error);
    } finally {
      setIsRetrying(null);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout pageTitle="ETL Tasks" pageDescription="Monitor and manage document processing tasks">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div
              className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
              style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
            />
            <p style={{ color: '#6B7280' }}>Loading ETL tasks...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="ETL Tasks" pageDescription="Monitor and manage document processing tasks">
      <div style={{ backgroundColor: '#F5F5F5' }}>
        <div className="w-full px-8 py-8 overflow-x-hidden">
        {/* Statistics */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <StatCard
                title="Total Tasks"
                value={statistics.total || 0}
                icon={
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                }
              />
              <StatCard
                title="Processing"
                value={statistics.processing || 0}
                icon={
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                  </svg>
                }
              />
              <StatCard
                title="Completed"
                value={statistics.completed || 0}
                icon={
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                }
              />
              <StatCard
                title="Failed"
                value={statistics.failed || 0}
                icon={
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                }
              />
            </div>
          )}

          {/* Action Bar with Search and Filters */}
          <div className="flex items-center justify-between gap-4 mb-6">
            {/* Search - Left */}
            <div className="relative flex-shrink-0" style={{ width: '300px' }}>
              <svg className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB', color: '#1F2937' }}
              />
            </div>

            {/* Filters & Actions - Right */}
            <div className="flex items-center gap-3 flex-shrink-0">
          <select
            value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB', color: '#1F2937' }}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
              <button
                onClick={refetch}
                className="px-4 py-2 rounded-lg border transition-all hover:bg-gray-50 whitespace-nowrap"
                style={{ borderColor: '#E5E7EB', color: '#1F2937' }}
              >
                Refresh
              </button>
            </div>
        </div>

        {/* Tasks Table */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
          <table className="w-full">
                <thead style={{ backgroundColor: '#F9FAFB' }}>
              <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B7280' }}>
                      Task ID
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B7280' }}>
                      Document ID
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B7280' }}>
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider hidden md:table-cell" style={{ color: '#6B7280' }}>
                      Created
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider hidden lg:table-cell" style={{ color: '#6B7280' }}>
                      Completed
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B7280' }}>
                      Actions
                    </th>
              </tr>
            </thead>
                <tbody className="divide-y" style={{ borderColor: '#E5E7EB' }}>
                  {paginatedTasks.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center">
                        <svg className="w-12 h-12 mx-auto mb-3" style={{ color: '#D1D5DB' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-sm" style={{ color: '#9CA3AF' }}>
                          {searchQuery || filterStatus !== 'all' ? 'No tasks found matching your filters' : 'No ETL tasks found'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    paginatedTasks.map((task) => {
                const statusColors = getStatusColor(task.status);
                return (
                        <tr key={task.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-medium" style={{ color: '#1F2937' }}>#{task.id}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm" style={{ color: '#6B7280' }}>#{task.document_id}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                      <span
                              className="px-2 py-1 rounded-full text-xs font-medium capitalize"
                        style={{ backgroundColor: statusColors.bg, color: statusColors.text }}
                      >
                        {task.status}
                      </span>
                    </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm hidden md:table-cell" style={{ color: '#6B7280' }}>
                      {new Date(task.created_at).toLocaleString()}
                    </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm hidden lg:table-cell" style={{ color: '#6B7280' }}>
                            {task.completed_at ? new Date(task.completed_at).toLocaleString() : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                      {task.status === 'failed' && (
                              <button
                                onClick={() => handleRetry(task.id)}
                                disabled={isRetrying === task.id}
                                className="px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-all hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ backgroundColor: COLORS.admin.primary }}
                        >
                                {isRetrying === task.id ? 'Retrying...' : 'Retry'}
                        </button>
                      )}
                            {task.status !== 'failed' && (
                              <span className="text-xs" style={{ color: '#9CA3AF' }}>-</span>
                      )}
                    </td>
                  </tr>
                );
                    })
                  )}
            </tbody>
          </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t flex flex-wrap items-center justify-between gap-4" style={{ borderColor: '#E5E7EB' }}>
                <div className="text-sm" style={{ color: '#6B7280' }}>
                  Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredTasks.length)} of {filteredTasks.length} tasks
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1.5 text-sm font-medium" style={{ color: '#6B7280' }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

